import React, { useState, useEffect, forwardRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  GraduationCap,
  Briefcase,
  MessageSquare,
  CreditCard,
  Calendar,
  UserCheck,
  Settings,
  LogOut,
  ChevronDown,
  User,
  HelpCircle,
  Building2,
  Users,
  UserPlus,
  Sparkles,
} from 'lucide-react';
import { Heading } from '@/Components/ui/typography';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/Components/ui/dropdown-menu';
import {
  Sidebar as ShadcnSidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarCollapseButton,
} from '@/Components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/Components/ui/collapsible';
import { useAuth } from '../../Hooks/useAuth';
import { cn } from '@/lib/utils';
import { useCompanyAccess } from '@/HireWithUs/Context/CompanyAccessContext';
import { SETTINGS_PERMISSIONS, firstSettingsPath } from '../Settings/settingsTabs';

// Grouped nav — three sections for scannability. Order within each section
// follows the recruiter's daily workflow: overview → jobs → sourcing →
// pipeline → interviews → comms → wrap-up. Account + Help stay pinned near
// the bottom because they're accessed rarely.
const NAV_SECTIONS = [
  {
    label: "Work",
    collapsible: false,
    items: [
      {
        icon: Calendar,
        text: "My interviews",
        path: "my-interviews",
        permission: "interviews.view",
        forInterviewers: true,
        interviewersOnly: true,
      },
      {
        icon: LayoutDashboard,
        text: "Overview",
        path: "overview",
        permission: "jobs.view",
      },
      {
        icon: FileText,
        text: "Post a Job",
        path: "postjob",
        permission: "jobs.create",
      },
      {
        icon: Briefcase,
        text: "All Jobs",
        path: "my-jobs",
        permission: "jobs.view",
      },
      {
        // Talent Match — AI-powered look-alike sourcing.
        // Recruiter pastes a LinkedIn URL and gets 10 similar candidates
        // from public data (Google index + GitHub).
        icon: Sparkles,
        text: "Talent Match",
        path: "backfill",
        permission: "sourcing.talent_match",
      },
      {
        icon: Calendar,
        text: "Scheduled Interviews",
        path: "interview",
        permission: "interviews.view",
      },
      {
        icon: MessageSquare,
        text: "Messages",
        path: "messages",
        permission: "messages.view",
      },
      {
        icon: UserCheck,
        text: "Offer & Onboarding",
        path: "hiring-lifecycle",
        permission: ["offers.view", "onboarding.view"],
      },
      {
        icon: UserCheck,
        text: "Hired Candidates",
        path: "hired-profiles",
        permission: "onboarding.view",
      },
    ],
  },
  {
    label: "Account",
    collapsible: true,
    items: [
      {
        // Single entry — Settings opens its own page with a secondary
        // sub-nav rail (Company Profile / Saved Interviewers / Recruiters).
        // A submenu here duplicated that rail, showing two nav lists side by
        // side. The dedicated settings shell owns the sub-navigation.
        icon: Settings,
        text: "Settings",
        path: "settings/companysetup",
        matchPrefix: "settings",
        permission: SETTINGS_PERMISSIONS,
        // Not everyone can open Company Profile, so go to the first tab they can.
        pathFor: (can) => firstSettingsPath(can).replace("/hirewithus/", ""),
      },
      {
        icon: CreditCard,
        text: "Plans & Billing",
        path: "billing",
        permission: "billing.view",
      },
    ],
  },
  {
    label: "Help",
    collapsible: true,
    items: [
      {
        icon: HelpCircle,
        text: "Customer Support",
        path: "Connect",
        forInterviewers: true,
      },
    ],
  },
];

// An Interviewer reaches single candidates, never whole jobs, so they get their
// interviews and help only. "My interviews" is theirs alone.
const showsForScope = (item, scope) =>
  scope === 'assigned_interviews' ? Boolean(item.forInterviewers) : !item.interviewersOnly;

// Flat view kept for the existing index-based active/expanded state logic.
const navItems = NAV_SECTIONS.flatMap((section) => section.items);


export const Sidebar = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [expandedItems, setExpandedItems] = useState({});
  // Which top-level sections are open. Collapsible sections start closed to
  // reduce visual load; the useEffect below auto-opens a section whenever
  // the current route lives inside one of its items.
  const [sectionOpen, setSectionOpen] = useState(() =>
    Object.fromEntries(NAV_SECTIONS.map((s) => [s.label, !s.collapsible]))
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const { can, user: teamMember, role, scope } = useCompanyAccess();
  const shows = (item) => can(item.permission) && showsForScope(item, scope);
  
  const handleNavigation = (index, path, disabled) => {
    if (disabled) return;
    setActiveIndex(index);
    navigate(`/hirewithus/${path}`);
  }

  const handleSubmenuNavigation = (subPath) => {
    navigate(`/hirewithus/${subPath}`);
  }

  const toggleSubmenu = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  }
  
  const handleLogout = async () => {
    // Await so the server session-clear POST + client storage nuke both
    // complete BEFORE we navigate. Otherwise navigation can race the async
    // logout() and drop the /api/logout request in flight.
    await logout();
    navigate('/hirewithus/login');
  }

  const handleLogoClick = () => {
    navigate('/hirewithus/overview');
  }

  const userDisplayName = teamMember?.name || user?.company_name || user?.employer_email || 'User';
  const userEmail = teamMember?.email || user?.employer_email || '';
  
  useEffect(() => {
    const currentItem = navItems.findIndex(item =>
      item.path && location.pathname.startsWith(`/hirewithus/${item.path}`)
    );
    if (currentItem !== -1) {
      setActiveIndex(currentItem);
    }

    // Check for active submenu items
    navItems.forEach((item, index) => {
      if (item.submenu) {
        const activeSubItem = item.submenu.find(subItem =>
          `/hirewithus/${subItem.path}` === location.pathname
        );
        if (activeSubItem) {
          setActiveIndex(index);
          setExpandedItems(prev => ({ ...prev, [index]: true }));
        }
      }
    });

    // Auto-open any collapsed section that contains the active route, so the
    // highlighted item stays visible. (We never auto-close — the user can
    // collapse it back manually.)
    NAV_SECTIONS.forEach((section) => {
      if (!section.collapsible) return;
      const matches = section.items.some((item) => {
        if (item.matchPrefix && location.pathname.startsWith(`/hirewithus/${item.matchPrefix}`)) {
          return true;
        }
        if (item.path && location.pathname.startsWith(`/hirewithus/${item.path}`)) {
          return true;
        }
        if (item.submenu) {
          return item.submenu.some(
            (sub) => `/hirewithus/${sub.path}` === location.pathname
          );
        }
        return false;
      });
      if (matches) {
        setSectionOpen((prev) => ({ ...prev, [section.label]: true }));
      }
    });
  }, [location.pathname]);

  const isItemActive = (index, item) => {
    if (index === activeIndex) return true;
    // matchPrefix lets one nav entry own a whole route family — e.g. the
    // single "Settings" link stays active across every /settings/* sub-page
    // (company, interviewers, recruiters) even though it navigates to just
    // one of them.
    if (item.matchPrefix && location.pathname.startsWith(`/hirewithus/${item.matchPrefix}`)) return true;
    if (item.path && location.pathname.startsWith(`/hirewithus/${item.path}`)) return true;
    if (item.submenu) {
      return item.submenu.some(subItem =>
        `/hirewithus/${subItem.path}` === location.pathname
      );
    }
    return false;
  }

  const isSubItemActive = (subPath) => {
    return `/hirewithus/${subPath}` === location.pathname;
  }

  return (
    <ShadcnSidebar collapsible="icon" className="border-r border-sidebar-border h-screen flex flex-col">
      <SidebarHeader className="border-b border-sidebar-border px-4 pt-5 pb-4 space-y-3 shrink-0 group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:pt-3 group-data-[collapsible=icon]:pb-3 group-data-[collapsible=icon]:space-y-0">
        <div className="flex items-center justify-between gap-2 group-data-[collapsible=icon]:justify-center">
          <button 
            type="button"
            className="flex items-center cursor-pointer transition-opacity hover:opacity-80 outline-none group-data-[collapsible=icon]:hidden"
            onClick={handleLogoClick}
          >
            <img
              src="/Newzenalphablue.png"
              alt="Company Logo"
              className="h-9 w-auto"
            />
          </button>
          <SidebarCollapseButton className="group-data-[collapsible=icon]:ml-0" />
        </div>
        <Heading 
          level="h2" 
          className="text-muted-foreground text-sm font-semibold tracking-wide uppercase group-data-[collapsible=icon]:hidden"
        >
          Employers Dashboard
        </Heading>
      </SidebarHeader>

      <SidebarContent className="px-3 flex-1 min-h-0">
        {NAV_SECTIONS.map((section, sectionIdx) => {
          // Offset so `index` stays global across sections — this keeps
          // activeIndex / expandedItems state (both indexed by global position)
          // working without a wider refactor.
          const offset = NAV_SECTIONS
            .slice(0, sectionIdx)
            .reduce((sum, s) => sum + s.items.length, 0);

          const isSectionOpen = section.collapsible
            ? Boolean(sectionOpen[section.label])
            : true;

          if (!section.items.some((item) => shows(item))) return null;

          return (
            <SidebarGroup key={section.label}>
              {section.collapsible ? (
                // Clickable header with chevron. Hidden when the sidebar is
                // in icon-only mode (labels aren't shown there anyway).
                <button
                  type="button"
                  onClick={() =>
                    setSectionOpen((prev) => ({
                      ...prev,
                      [section.label]: !prev[section.label],
                    }))
                  }
                  className="w-full flex items-center justify-between px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 hover:text-foreground transition-colors group-data-[collapsible=icon]:hidden rounded-md"
                >
                  <span>{section.label}</span>
                  <ChevronDown
                    className={cn(
                      "h-3.5 w-3.5 transition-transform duration-200",
                      !isSectionOpen && "-rotate-90"
                    )}
                  />
                </button>
              ) : (
                <SidebarGroupLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 group-data-[collapsible=icon]:hidden">
                  {section.label}
                </SidebarGroupLabel>
              )}
              {/* Items always render in icon-mode (icons are the only surface
                  there). In expanded mode, hide when a collapsible section
                  is closed. */}
              <SidebarGroupContent
                className={cn(
                  section.collapsible && !isSectionOpen && "hidden group-data-[collapsible=icon]:block"
                )}
              >
                <SidebarMenu className="gap-1.5">
                  {section.items.map((item, i) => {
                    // Skipped rather than filtered out, so index stays global.
                    if (!shows(item)) return null;
                    const index = offset + i;
                    const Icon = item.icon;
                    const isActive = isItemActive(index, item);
                    const isExpanded = expandedItems[index];

                    if (item.submenu) {
                      return (
                        <Collapsible
                          key={index}
                          open={isExpanded}
                          onOpenChange={(open) => {
                            setExpandedItems(prev => ({ ...prev, [index]: open }));
                          }}
                        >
                          <SidebarMenuItem>
                            <CollapsibleTrigger asChild>
                              <SidebarMenuButton
                                isActive={isActive}
                                disabled={item.disabled}
                                size="lg"
                                className="group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:justify-center"
                              >
                                <Icon />
                                <span className="group-data-[collapsible=icon]:hidden">{item.text}</span>
                                <ChevronDown className={cn(
                                  "ml-auto h-4 w-4 transition-transform duration-200 group-data-[collapsible=icon]:hidden",
                                  isExpanded && "rotate-180"
                                )} />
                              </SidebarMenuButton>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down mt-1 group-data-[collapsible=icon]:hidden">
                              <SidebarMenuSub>
                                {item.submenu.map((subItem, subIndex) => {
                                  const SubIcon = subItem.icon;
                                  return (
                                    <SidebarMenuSubItem key={subIndex}>
                                      <SidebarMenuSubButton
                                        isActive={isSubItemActive(subItem.path)}
                                        onClick={() => handleSubmenuNavigation(subItem.path)}
                                      >
                                        {SubIcon && <SubIcon className="h-4 w-4 shrink-0" />}
                                        <span>{subItem.text}</span>
                                      </SidebarMenuSubButton>
                                    </SidebarMenuSubItem>
                                  );
                                })}
                              </SidebarMenuSub>
                            </CollapsibleContent>
                          </SidebarMenuItem>
                        </Collapsible>
                      );
                    }

                    return (
                      <SidebarMenuItem key={index}>
                        <SidebarMenuButton
                          isActive={isActive}
                          disabled={item.disabled}
                          onClick={() => handleNavigation(index, item.pathFor ? item.pathFor(can) : item.path, item.disabled)}
                          size="lg"
                          className="group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:justify-center"
                        >
                          <Icon />
                          <span className="group-data-[collapsible=icon]:hidden">{item.text}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border px-3 py-4 shrink-0 mt-auto group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:py-3">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-hidden ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 w-full justify-start gap-3 h-auto py-2.5 px-3 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:p-2"
                >
                  <div className="flex items-center justify-center h-9 w-9 rounded-full bg-sidebar-accent text-sidebar-accent-foreground shrink-0 border border-sidebar-border">
                    <User className="h-4 w-4" />
                  </div>
                  <div className="flex flex-col items-start text-left flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                    <span className="text-sm font-medium text-sidebar-foreground truncate w-full">
                      {userDisplayName}
                    </span>
                    {userEmail && (
                      <span className="text-xs text-muted-foreground truncate w-full">
                        {userEmail}
                      </span>
                    )}
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0 ml-auto group-data-[collapsible=icon]:hidden" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                side="top"
                sideOffset={8}
                className="w-56 z-[100]"
              >
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{userDisplayName}</p>
                    {userEmail && (
                      <p className="text-xs leading-none text-muted-foreground">
                        {userEmail}
                      </p>
                    )}
                    {teamMember && role?.name && (
                      <p className="text-xs leading-none text-muted-foreground">{role.name}</p>
                    )}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </ShadcnSidebar>
  );
};
